import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type { Contact, ContactInput } from "@websede/contracts";
import {
  ArrowLeft,
  Building2,
  Mail,
  Pencil,
  Phone,
  Plus,
  Search,
  StickyNote,
  Trash2,
  User,
  UserPlus,
  UserRoundSearch,
  Users,
  X,
} from "lucide-react";
import { type FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "../api/client.js";
import { Avatar } from "../components/avatar.js";
import { CopyValue } from "../components/copy-value.js";
import { ErrorNotice } from "../components/form.js";
import { ShellSearch, ShellSidebar, useShell } from "../components/shell-slots.js";

interface Draft extends ContactInput {
  id?: string;
}

type Mode = { kind: "list" } | { kind: "view"; id: string } | { kind: "edit"; draft: Draft };

/** The address book the API fills with people the user has written to (see other-contacts.ts). */
export const OTHER_CONTACTS_BOOK = "Other contacts";

export function displayName(contact: Pick<Contact, "fullName" | "emails" | "phones">): string {
  return contact.fullName || contact.emails[0] || contact.phones[0] || "";
}

/** Contacts grouped under the first letter of their name, in the user's language order. */
export function groupByLetter<T extends Pick<Contact, "fullName" | "emails" | "phones">>(
  contacts: T[],
  locale: string,
): Array<{ letter: string; contacts: T[] }> {
  const collator = new Intl.Collator(locale, { sensitivity: "base" });
  const sorted = [...contacts].sort((a, b) => collator.compare(displayName(a), displayName(b)));
  const groups: Array<{ letter: string; contacts: T[] }> = [];
  for (const contact of sorted) {
    const first = displayName(contact).trim()[0]?.toLocaleUpperCase(locale) ?? "#";
    // Where the language treats an accented letter as the plain one (C and Ç in English), label
    // the group with the plain letter; where it is its own letter (Turkish), keep it.
    const plain = first.normalize("NFD").replace(/\p{M}/gu, "");
    const letter = !/\p{L}/u.test(first)
      ? "#"
      : collator.compare(first, plain) === 0
        ? plain
        : first;
    const last = groups.at(-1);
    if (last && collator.compare(last.letter, letter) === 0) last.contacts.push(contact);
    else groups.push({ letter, contacts: [contact] });
  }
  return groups;
}

/** Google Contacts' layout: the list across the page, a contact opens full width. */
export function ContactsPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const { closeDrawer } = useShell();
  const [search, setSearch] = useState("");
  const [mode, setMode] = useState<Mode>({ kind: "list" });
  const [showOther, setShowOther] = useState(false);
  const books = useQuery({ queryKey: ["contacts", "books"], queryFn: () => api.contacts.books() });
  const contacts = useQuery({
    queryKey: ["contacts", "list", search],
    queryFn: () => api.contacts.list(search),
  });
  const all = useQuery({
    queryKey: ["contacts", "list", ""],
    queryFn: () => api.contacts.list(""),
  });
  const otherBookId = books.data?.addressBooks.find((b) => b.name === OTHER_CONTACTS_BOOK)?.id;
  const defaultBook =
    books.data?.addressBooks.find((b) => b.isDefault && b.id !== otherBookId) ??
    books.data?.addressBooks.find((b) => b.id !== otherBookId);
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["contacts", "list"] });
  const list = useMemo(
    () =>
      (contacts.data?.contacts ?? []).filter(
        (c) => (c.addressBookId === otherBookId) === showOther,
      ),
    [contacts.data, otherBookId, showOther],
  );
  const groups = useMemo(() => groupByLetter(list, i18n.language), [list, i18n.language]);
  const total = (all.data?.contacts ?? []).filter((c) => c.addressBookId !== otherBookId).length;
  const otherTotal = (all.data?.contacts ?? []).filter(
    (c) => c.addressBookId === otherBookId,
  ).length;

  const create = () => {
    closeDrawer();
    setMode({
      kind: "edit",
      draft: {
        addressBookId: defaultBook?.id ?? "",
        givenName: "",
        familyName: "",
        emails: [],
        phones: [],
        organization: null,
        notes: null,
      },
    });
  };
  const viewing =
    mode.kind === "view" ? (all.data?.contacts ?? list).find((c) => c.id === mode.id) : undefined;

  return (
    <div className="relative flex h-full min-h-0 flex-col">
      <ShellSearch>
        <div className="flex h-full min-w-0 flex-1 items-center">
          <Search className="ml-4 hidden size-5 shrink-0 text-ink-2 md:block" />
          <input
            type="search"
            className="h-full min-w-0 flex-1 bg-transparent px-2 text-base outline-none placeholder:text-ink-2 md:px-3"
            placeholder={t("contacts.search")}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setMode({ kind: "list" });
            }}
          />
          {search ? (
            <button
              type="button"
              aria-label={t("mail.clearSearch")}
              className="flex size-10 shrink-0 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
              onClick={() => setSearch("")}
            >
              <X className="size-5" />
            </button>
          ) : null}
        </div>
      </ShellSearch>

      <ShellSidebar>
        <div className="hidden px-2 pt-1 pb-4 md:block">
          <button
            type="button"
            onClick={create}
            className="flex h-14 items-center gap-3 rounded-2xl bg-accent-soft pr-6 pl-4 text-sm font-medium text-accent hover:shadow-1"
          >
            <UserPlus className="size-5" />
            {t("contacts.create")}
          </button>
        </div>
        <nav className="flex flex-col pr-2 md:pr-3">
          {(
            [
              [false, t("contacts.all"), total, Users],
              [true, t("contacts.other"), otherTotal, UserRoundSearch],
            ] as const
          ).map(([other, label, count, Icon]) => (
            <button
              key={label}
              type="button"
              onClick={() => {
                setShowOther(other);
                setSearch("");
                setMode({ kind: "list" });
                closeDrawer();
              }}
              className={`flex h-9 items-center gap-4 rounded-r-full pr-3 pl-6 text-left text-sm ${showOther === other ? "bg-accent-soft font-medium text-accent" : "text-ink-2 hover:bg-ink/5"}`}
            >
              <Icon className="size-[18px]" strokeWidth={showOther === other ? 2.2 : 1.8} />
              <span className="flex-1">{label}</span>
              <span className="text-xs tabular-nums">{count}</span>
            </button>
          ))}
        </nav>
      </ShellSidebar>

      {mode.kind === "edit" ? (
        <ContactEditor
          draft={mode.draft}
          onChange={(draft) => setMode({ kind: "edit", draft })}
          onCancel={() =>
            setMode(mode.draft.id ? { kind: "view", id: mode.draft.id } : { kind: "list" })
          }
          onSaved={(saved) => {
            void refresh();
            setMode({ kind: "view", id: saved.id });
          }}
        />
      ) : mode.kind === "view" && viewing ? (
        <ContactCard
          contact={viewing}
          promoteTo={viewing.addressBookId === otherBookId ? defaultBook?.id : undefined}
          onPromoted={() => void refresh()}
          onBack={() => setMode({ kind: "list" })}
          onEdit={() => setMode({ kind: "edit", draft: { ...viewing } })}
          onDeleted={() => {
            void refresh();
            setMode({ kind: "list" });
          }}
        />
      ) : (
        <div className="min-h-0 flex-1 overflow-auto pb-24 md:pb-4">
          <div className="flex h-14 items-center px-4 md:px-6">
            <h1 className="text-lg text-ink md:text-[22px]">
              {search ? t("mail.results") : showOther ? t("contacts.other") : t("contacts.all")}
              <span className="ml-2 text-sm text-ink-3">({list.length})</span>
            </h1>
          </div>
          {showOther && !search ? (
            <p className="-mt-2 px-4 pb-2 text-sm text-ink-3 md:px-6">{t("contacts.otherHint")}</p>
          ) : null}
          <ErrorNotice error={contacts.error} />
          <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1.5fr)] border-b border-rule px-6 pb-2 text-xs font-medium text-ink-2 md:grid">
            <span>{t("contacts.name")}</span>
            <span>{t("contacts.email")}</span>
            <span>{t("contacts.phone")}</span>
            <span>{t("contacts.organization")}</span>
          </div>
          {groups.map((group) => (
            <section key={group.letter} className="flex md:block">
              <div className="w-12 shrink-0 pt-3 text-center text-sm font-medium text-accent md:hidden">
                {group.letter}
              </div>
              <ul className="min-w-0 flex-1">
                {group.contacts.map((c) => (
                  <li key={c.id}>
                    <button
                      type="button"
                      onClick={() => setMode({ kind: "view", id: c.id })}
                      className="flex w-full items-center gap-3 py-2 pr-4 text-left hover:bg-ink/[0.04] md:grid md:grid-cols-[minmax(0,2fr)_minmax(0,2fr)_minmax(0,1.3fr)_minmax(0,1.5fr)] md:gap-0 md:px-6"
                    >
                      <span className="flex min-w-0 items-center gap-3">
                        <Avatar name={displayName(c) || "?"} size={36} />
                        <span className="min-w-0">
                          <span className="block truncate text-sm text-ink">
                            {displayName(c) || t("contacts.noName")}
                          </span>
                          <span className="block truncate text-xs text-ink-3 md:hidden">
                            {c.organization || c.emails[0] || c.phones[0] || ""}
                          </span>
                        </span>
                      </span>
                      <span className="hidden truncate pr-4 text-sm text-ink-2 md:block">
                        {c.emails[0] ?? ""}
                      </span>
                      <span className="hidden truncate pr-4 text-sm text-ink-2 md:block">
                        {c.phones[0] ?? ""}
                      </span>
                      <span className="hidden truncate text-sm text-ink-2 md:block">
                        {c.organization ?? ""}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {contacts.data && list.length === 0 ? (
            <div className="flex flex-col items-center gap-3 px-6 py-16 text-center">
              <Users className="size-12 text-ink-3" strokeWidth={1.4} />
              <p className="text-sm text-ink-3">
                {search
                  ? t("contacts.noMatch")
                  : showOther
                    ? t("contacts.otherEmpty")
                    : t("contacts.empty")}
              </p>
              {search || showOther ? null : (
                <button
                  type="button"
                  onClick={create}
                  className="rounded-full bg-accent px-5 py-2 text-sm font-medium text-white"
                >
                  {t("contacts.create")}
                </button>
              )}
            </div>
          ) : null}
        </div>
      )}

      {mode.kind === "list" ? (
        <button
          type="button"
          aria-label={t("contacts.create")}
          onClick={create}
          className="absolute right-4 bottom-4 flex size-14 items-center justify-center rounded-2xl bg-accent-soft text-accent shadow-2 md:hidden"
        >
          <UserPlus className="size-6" />
        </button>
      ) : null}
    </div>
  );
}

/** One contact, read-only, with quick actions like Google Contacts. */
function ContactCard({
  contact,
  promoteTo,
  onPromoted,
  onBack,
  onEdit,
  onDeleted,
}: {
  contact: Contact;
  /** Set for other contacts: the address book "Add to contacts" moves them into. */
  promoteTo?: string | undefined;
  onPromoted: () => void;
  onBack: () => void;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const { t } = useTranslation();
  const remove = useMutation({
    mutationFn: () => api.contacts.remove(contact.id),
    onSuccess: onDeleted,
  });
  const promote = useMutation({
    mutationFn: () => api.contacts.update(contact.id, { addressBookId: promoteTo ?? "" }),
    onSuccess: onPromoted,
  });
  const name = displayName(contact) || t("contacts.noName");
  const email = contact.emails[0];
  const phone = contact.phones[0];
  const action =
    "flex flex-col items-center gap-1.5 text-xs text-ink-2 [&>span:first-child]:flex [&>span:first-child]:size-12 [&>span:first-child]:items-center [&>span:first-child]:justify-center [&>span:first-child]:rounded-full [&>span:first-child]:bg-accent-soft [&>span:first-child]:text-accent";

  return (
    <article className="min-h-0 flex-1 overflow-auto pb-16">
      <div className="flex h-12 items-center gap-1 px-2">
        <button
          type="button"
          aria-label={t("mail.back")}
          className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
          onClick={onBack}
        >
          <ArrowLeft className="size-5" />
        </button>
        <span className="flex-1" />
        {promoteTo ? (
          <button
            type="button"
            disabled={promote.isPending}
            onClick={() => promote.mutate()}
            className="mr-1 flex h-9 items-center gap-2 rounded-full border border-ink-3/40 px-4 text-sm font-medium text-accent hover:bg-accent-soft/60"
          >
            <UserPlus className="size-4" />
            {t("contacts.addToContacts")}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onEdit}
          className="flex h-9 items-center gap-2 rounded-full bg-accent px-5 text-sm font-medium text-white"
        >
          <Pencil className="size-4" />
          {t("contacts.edit")}
        </button>
        <button
          type="button"
          aria-label={t("contacts.delete")}
          title={t("contacts.delete")}
          disabled={remove.isPending}
          className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5 hover:text-danger"
          onClick={() => {
            if (window.confirm(t("contacts.confirmDelete", { name }))) remove.mutate();
          }}
        >
          <Trash2 className="size-5" />
        </button>
      </div>

      <div className="flex flex-col items-center gap-3 px-4 pt-2 md:flex-row md:items-end md:gap-6 md:px-10">
        <Avatar name={name} size={120} />
        <div className="text-center md:pb-3 md:text-left">
          <h1 className="text-[28px] leading-tight text-ink">{name}</h1>
          {contact.organization ? (
            <p className="mt-1 text-sm text-ink-2">{contact.organization}</p>
          ) : null}
        </div>
      </div>

      <div className="mt-6 flex justify-center gap-8 px-4 md:justify-start md:px-10">
        {email ? (
          <Link to="/mail" search={{ to: email }} className={action}>
            <span>
              <Mail className="size-5" />
            </span>
            <span>{t("contacts.sendEmail")}</span>
          </Link>
        ) : null}
        {phone ? (
          <a href={`tel:${phone.replace(/[^\d+]/g, "")}`} className={action}>
            <span>
              <Phone className="size-5" />
            </span>
            <span>{t("contacts.call")}</span>
          </a>
        ) : null}
        <button type="button" onClick={onEdit} className={action}>
          <span>
            <Pencil className="size-5" />
          </span>
          <span>{t("contacts.edit")}</span>
        </button>
      </div>

      <section className="mx-4 mt-8 max-w-2xl rounded-2xl border border-rule p-4 md:mx-10">
        <h2 className="mb-2 text-sm font-medium text-ink">{t("contacts.details")}</h2>
        {contact.emails.map((value) => (
          <div key={value} className="flex items-center gap-4 py-1.5">
            <Mail className="size-5 shrink-0 text-ink-3" />
            <Link
              to="/mail"
              search={{ to: value }}
              className="min-w-0 flex-1 truncate text-sm text-accent hover:underline"
            >
              {value}
            </Link>
            <CopyValue value={value} />
          </div>
        ))}
        {contact.phones.map((value) => (
          <div key={value} className="flex items-center gap-4 py-1.5">
            <Phone className="size-5 shrink-0 text-ink-3" />
            <a
              href={`tel:${value.replace(/[^\d+]/g, "")}`}
              className="min-w-0 flex-1 truncate text-sm text-ink hover:underline"
            >
              {value}
            </a>
            <CopyValue value={value} />
          </div>
        ))}
        {contact.organization ? (
          <div className="flex items-center gap-4 py-1.5">
            <Building2 className="size-5 shrink-0 text-ink-3" />
            <span className="text-sm text-ink">{contact.organization}</span>
          </div>
        ) : null}
        {contact.notes ? (
          <div className="flex items-start gap-4 py-1.5">
            <StickyNote className="mt-0.5 size-5 shrink-0 text-ink-3" />
            <p className="text-sm whitespace-pre-wrap text-ink">{contact.notes}</p>
          </div>
        ) : null}
        {contact.emails.length + contact.phones.length === 0 &&
        !contact.organization &&
        !contact.notes ? (
          <p className="text-sm text-ink-3">{t("contacts.noDetails")}</p>
        ) : null}
      </section>
      <div className="mx-4 md:mx-10">
        <ErrorNotice error={remove.error ?? promote.error} />
      </div>
    </article>
  );
}

/** A list of values edited one field each, with a button to add another. */
function MultiField({
  values,
  type,
  placeholder,
  addLabel,
  onChange,
}: {
  values: string[];
  type: "email" | "tel";
  placeholder: string;
  addLabel: string;
  onChange: (values: string[]) => void;
}) {
  const { t } = useTranslation();
  const rows = values.length > 0 ? values : [""];
  return (
    <div className="flex min-w-0 flex-1 flex-col gap-2">
      {rows.map((value, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: rows have no identity besides their position
        <div key={index} className="flex items-center gap-1">
          <input
            type={type}
            aria-label={placeholder}
            placeholder={placeholder}
            className="h-11 min-w-0 flex-1 rounded-lg border border-rule bg-surface px-3 text-sm outline-none focus:border-accent"
            value={value}
            onChange={(e) => {
              const next = [...rows];
              next[index] = e.target.value;
              onChange(next);
            }}
          />
          {rows.length > 1 || value ? (
            <button
              type="button"
              aria-label={t("contacts.removeValue")}
              className="flex size-9 shrink-0 items-center justify-center rounded-full text-ink-3 hover:bg-ink/5"
              onClick={() => onChange(rows.filter((_, i) => i !== index))}
            >
              <X className="size-4" />
            </button>
          ) : null}
        </div>
      ))}
      <button
        type="button"
        className="flex items-center gap-1.5 self-start rounded-full px-2 py-1 text-sm text-accent hover:bg-accent-soft"
        onClick={() => onChange([...rows, ""])}
      >
        <Plus className="size-4" />
        {addLabel}
      </button>
    </div>
  );
}

function ContactEditor({
  draft,
  onChange,
  onCancel,
  onSaved,
}: {
  draft: Draft;
  onChange: (next: Draft) => void;
  onCancel: () => void;
  onSaved: (saved: Contact) => void;
}) {
  const { t } = useTranslation();
  const save = useMutation({
    mutationFn: () => {
      const { id, ...rest } = draft;
      const input = {
        ...rest,
        emails: rest.emails.map((e) => e.trim()).filter(Boolean),
        phones: rest.phones.map((p) => p.trim()).filter(Boolean),
      };
      return id ? api.contacts.update(id, input) : api.contacts.create(input);
    },
    onSuccess: onSaved,
  });
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    save.mutate();
  };
  const name = `${draft.givenName} ${draft.familyName}`.trim();
  const input =
    "h-11 min-w-0 flex-1 rounded-lg border border-rule bg-surface px-3 text-sm outline-none focus:border-accent";
  // In the stacked phone layout the name fields take the full width instead of flexing.
  const nameInput =
    "h-11 w-full min-w-0 rounded-lg border border-rule bg-surface px-3 text-sm outline-none focus:border-accent sm:flex-1";
  const row = "flex items-start gap-4";
  const icon = "mt-3 size-5 shrink-0 text-ink-3";

  return (
    <form onSubmit={onSubmit} className="min-h-0 flex-1 overflow-auto pb-16">
      <div className="flex h-12 items-center gap-1 px-2">
        <button
          type="button"
          aria-label={t("common.cancel")}
          className="flex size-10 items-center justify-center rounded-full text-ink-2 hover:bg-ink/5"
          onClick={onCancel}
        >
          <X className="size-5" />
        </button>
        <span className="flex-1 truncate px-2 text-base text-ink">
          {draft.id ? t("contacts.editContact") : t("contacts.newContact")}
        </span>
        <button
          type="submit"
          disabled={save.isPending}
          className="h-9 rounded-full bg-accent px-6 text-sm font-medium text-white disabled:opacity-60"
        >
          {t("common.save")}
        </button>
      </div>
      <div className="mx-auto flex max-w-2xl flex-col gap-5 px-4 pt-4 md:px-8">
        <div className="flex justify-center pb-2">
          <Avatar name={name || draft.emails[0] || "?"} size={96} />
        </div>
        <div className={row}>
          <User className={icon} />
          <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
            <input
              // biome-ignore lint/a11y/noAutofocus: the name is the first thing to type
              autoFocus={!draft.id}
              aria-label={t("contacts.givenName")}
              placeholder={t("contacts.givenName")}
              className={nameInput}
              value={draft.givenName}
              onChange={(e) => onChange({ ...draft, givenName: e.target.value })}
            />
            <input
              aria-label={t("contacts.familyName")}
              placeholder={t("contacts.familyName")}
              className={nameInput}
              value={draft.familyName}
              onChange={(e) => onChange({ ...draft, familyName: e.target.value })}
            />
          </div>
        </div>
        <div className={row}>
          <Building2 className={icon} />
          <input
            aria-label={t("contacts.organization")}
            placeholder={t("contacts.organization")}
            className={input}
            value={draft.organization ?? ""}
            onChange={(e) => onChange({ ...draft, organization: e.target.value || null })}
          />
        </div>
        <div className={row}>
          <Mail className={icon} />
          <MultiField
            type="email"
            values={draft.emails}
            placeholder={t("contacts.email")}
            addLabel={t("contacts.addEmail")}
            onChange={(emails) => onChange({ ...draft, emails })}
          />
        </div>
        <div className={row}>
          <Phone className={icon} />
          <MultiField
            type="tel"
            values={draft.phones}
            placeholder={t("contacts.phone")}
            addLabel={t("contacts.addPhone")}
            onChange={(phones) => onChange({ ...draft, phones })}
          />
        </div>
        <div className={row}>
          <StickyNote className={icon} />
          <textarea
            aria-label={t("contacts.notes")}
            placeholder={t("contacts.notes")}
            className="min-h-24 min-w-0 flex-1 rounded-lg border border-rule bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
            value={draft.notes ?? ""}
            onChange={(e) => onChange({ ...draft, notes: e.target.value || null })}
          />
        </div>
        <ErrorNotice error={save.error} />
      </div>
    </form>
  );
}
